import type { IKeyboardShortcut, IKeyboardShortcutAmount, ISettings } from '@/shared/interfaces';
import { SETTINGS_DEFAULT } from '@/shared/settingsDefaults';
import { isFogo } from '@/shared/appEnvironment';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export type ShortcutAmountAction = IKeyboardShortcutAmount['action'];

const SHORTCUT_AMOUNT_ACTIONS: ShortcutAmountAction[] = ['buy', 'sell'];

const createDefaultSettings = (): ISettings => {
	return JSON.parse(JSON.stringify(SETTINGS_DEFAULT));
};

const mergeStoredShortcut = (
	defaultShortcut: IKeyboardShortcut,
	storedShortcut?: IKeyboardShortcut
): IKeyboardShortcut => {
	return { ...defaultShortcut, ...storedShortcut };
};

const createSettingsFromStorage = (storage: string): ISettings => {
	const loadedSettings = JSON.parse(storage);
	if (!loadedSettings || typeof loadedSettings !== 'object' || Array.isArray(loadedSettings)) {
		throw new Error('Invalid persisted settings payload');
	}

	const defaults = createDefaultSettings();
	const storedKeyboardShortcuts = loadedSettings.keyboardShortcuts;
	const storedShortcuts = storedKeyboardShortcuts?.shortcuts;

	return {
		...defaults,
		...loadedSettings,
		swapValues: {
			...defaults.swapValues,
			...loadedSettings.swapValues,
		},
		keyboardShortcuts: {
			...defaults.keyboardShortcuts,
			...storedKeyboardShortcuts,
			shortcuts: {
				search: mergeStoredShortcut(
					defaults.keyboardShortcuts.shortcuts.search,
					storedShortcuts?.search
				),
				sell: mergeStoredShortcut(defaults.keyboardShortcuts.shortcuts.sell, storedShortcuts?.sell),
				buy: mergeStoredShortcut(defaults.keyboardShortcuts.shortcuts.buy, storedShortcuts?.buy),
				cycleIntervalLeft: mergeStoredShortcut(
					defaults.keyboardShortcuts.shortcuts.cycleIntervalLeft,
					storedShortcuts?.cycleIntervalLeft
				),
				cycleIntervalRight: mergeStoredShortcut(
					defaults.keyboardShortcuts.shortcuts.cycleIntervalRight,
					storedShortcuts?.cycleIntervalRight
				),
				help: mergeStoredShortcut(defaults.keyboardShortcuts.shortcuts.help, storedShortcuts?.help),
			},
			customAmountShortcuts: Array.isArray(storedKeyboardShortcuts?.customAmountShortcuts)
				? storedKeyboardShortcuts.customAmountShortcuts
				: defaults.keyboardShortcuts.customAmountShortcuts,
		},
	};
};

const isValidShortcutAmount = (
	amount: IKeyboardShortcutAmount | undefined,
	action: ShortcutAmountAction
): amount is IKeyboardShortcutAmount => {
	if (!amount) return false;
	if (amount.action !== action) return false;
	if (amount.type !== 'fixed' && amount.type !== 'percentage') return false;

	return Number.isFinite(amount.value) && amount.value >= 0;
};

export const getDefaultShortcutAmount = (action: ShortcutAmountAction): IKeyboardShortcutAmount => {
	const amount = SETTINGS_DEFAULT.keyboardShortcuts.shortcuts[action].amount;

	if (!isValidShortcutAmount(amount, action)) {
		throw new Error(`Invalid default amount for ${action} shortcut`);
	}

	return { ...amount };
};

const normalizeShortcutAmount = (settings: ISettings, action: ShortcutAmountAction): ISettings => {
	const shortcut = settings.keyboardShortcuts.shortcuts[action];

	if (isValidShortcutAmount(shortcut.amount, action)) return settings;

	return {
		...settings,
		keyboardShortcuts: {
			...settings.keyboardShortcuts,
			shortcuts: {
				...settings.keyboardShortcuts.shortcuts,
				[action]: {
					...shortcut,
					amount: getDefaultShortcutAmount(action),
				},
			},
		},
	};
};

const normalizeShortcutAmounts = (settings: ISettings): ISettings => {
	return SHORTCUT_AMOUNT_ACTIONS.reduce(normalizeShortcutAmount, settings);
};

const assertShortcutAmounts = (settings: ISettings): void => {
	const invalidAction = SHORTCUT_AMOUNT_ACTIONS.find((action) => {
		return !isValidShortcutAmount(settings.keyboardShortcuts.shortcuts[action].amount, action);
	});

	if (invalidAction) {
		throw new Error(`Invalid amount state for ${invalidAction} shortcut`);
	}
};

const normalizeExplorerForEnvironment = (settings: ISettings, shouldBeFogo: boolean): ISettings => {
	const isFogoExplorer = settings.explorer.includes('fogo');

	if (isFogoExplorer === shouldBeFogo) return settings;

	return {
		...settings,
		explorer: shouldBeFogo ? 'https://explorer.fogo.io' : 'https://solscan.io',
	};
};

export const useSettingsStore = defineStore('settings', () => {
	// data
	const settings = ref<ISettings>(createDefaultSettings());

	// methods
	const init = (): void => {
		const storage = localStorage.getItem('fluxbeam:v1:settings');

		if (!storage) return;

		const restoredSettings = normalizeShortcutAmounts(createSettingsFromStorage(storage));
		const shouldBeFogo = isFogo();
		const normalizedSettings = normalizeExplorerForEnvironment(restoredSettings, shouldBeFogo);

		settings.value = normalizedSettings;

		if (normalizedSettings.explorer !== restoredSettings.explorer) {
			console.log(
				`🔄 Explorer URL corrected to match ${shouldBeFogo ? 'FOGO' : 'Solana'} environment: ${
					normalizedSettings.explorer
				}`
			);
			localStorage.setItem('fluxbeam:v1:settings', JSON.stringify(normalizedSettings));
		}
	};

	const update = (): void => {
		assertShortcutAmounts(settings.value);

		// Save to localStorage
		localStorage.setItem('fluxbeam:v1:settings', JSON.stringify(settings.value));
	};

	const reset = (): void => {
		localStorage.removeItem('fluxbeam:v1:settings');
		settings.value = createDefaultSettings();

		// Update localStorage with the reset settings
		update();
	};

	// setup
	init();

	// getter
	const getSettings = computed<ISettings>(() => settings.value);
	const getSwapSellValuesSorted = computed<number[]>(() => {
		return settings.value.swapValues.sell;
	});
	const getSwapBuyValuesSorted = computed<number[]>(() => {
		return settings.value.swapValues.buy;
	});

	return {
		settings,
		init,
		update,
		reset,
		getSettings,
		getSwapSellValuesSorted,
		getSwapBuyValuesSorted,
	};
});
